import { useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import Logo from "../../component/layout/Logo";
import "../../styles/pages/auth/_timeout.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const LAST_ACTIVITY_KEY = "moozLastActivityAt";
const TIMEOUT_RETURN_TO_KEY = "moozTimeoutReturnTo";

// ─── Decorative SVG — abstract session/lock motif ────────────────────────────
function TimeoutGraphic() {
    return (
        <svg
            className="to-graphic"
            viewBox="0 0 340 340"
            fill="none"
            aria-hidden="true"
        >
            <defs>
                <radialGradient id="tog_glow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#5b4fcf" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#5b4fcf" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="tog_ring" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.12)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </radialGradient>
            </defs>

            {/* Ambient glow */}
            <circle cx="170" cy="180" r="170" fill="url(#tog_glow)" />

            {/* Concentric dashed rings */}
            <circle
                cx="170"
                cy="185"
                r="140"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
                strokeDasharray="4 8"
            />
            <circle
                cx="170"
                cy="185"
                r="108"
                stroke="rgba(255,255,255,0.09)"
                strokeWidth="1"
                strokeDasharray="3 6"
            />
            <circle
                cx="170"
                cy="185"
                r="76"
                stroke="rgba(255,255,255,0.12)"
                strokeWidth="1"
            />

            {/* Central lock shape */}
            <rect
                x="140"
                y="175"
                width="60"
                height="46"
                rx="8"
                fill="rgba(255,255,255,0.1)"
                stroke="rgba(255,255,255,0.22)"
                strokeWidth="1.5"
            />
            <path
                d="M153 175v-14a17 17 0 0 1 34 0v14"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth="1.8"
                strokeLinecap="round"
            />
            <circle cx="170" cy="198" r="5" fill="rgba(255,255,255,0.5)" />
            <rect
                x="168.5"
                y="201"
                width="3"
                height="9"
                rx="1.5"
                fill="rgba(255,255,255,0.4)"
            />

            {/* Tick marks on outer ring */}
            {/* {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i * 30 - 90) * (Math.PI / 180);
                const inner = 128;
                const outer = i % 3 === 0 ? 138 : 133;
                return (
                    <line
                        key={i}
                        x1={170 + inner * Math.cos(angle)}
                        y1={170 + inner * Math.sin(angle)}
                        x2={170 + outer * Math.cos(angle)}
                        y2={170 + outer * Math.sin(angle)}
                        stroke="rgba(255,255,255,0.25)"
                        strokeWidth={i % 3 === 0 ? 2 : 1}
                        strokeLinecap="round"
                    />
                );
            })} */}
        </svg>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function Timeout() {
    const { loginWithRedirect, isLoading } = useAuth0();

    const returnTo = useMemo(() => {
        const stored = localStorage.getItem(TIMEOUT_RETURN_TO_KEY);
        return stored && stored.startsWith("/") ? stored : "/dashboard";
    }, []);

    const handleLogin = useCallback(() => {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem(TIMEOUT_RETURN_TO_KEY);
        loginWithRedirect({ appState: { returnTo } });
    }, [loginWithRedirect, returnTo]);

    return (
        <div
            className="to-page"
            role="dialog"
            aria-modal="true"
            aria-labelledby="to-title"
            aria-describedby="to-desc"
        >
            {/* ── Background layer ── */}
            <div className="to-bg" aria-hidden="true">
                <div className="to-bg__orb to-bg__orb--1" />
                <div className="to-bg__orb to-bg__orb--2" />
                <div className="to-bg__orb to-bg__orb--3" />
                <div className="to-bg__grid" />
            </div>

            {/* ── Logo — top left ── */}
            <div className="to-logo-wrap">
                <Link to="/" aria-label="Go to Mooz home">
                    <Logo />
                </Link>
            </div>

            {/* ── Central card ── */}
            <article className="to-card">
                {/* Graphic */}
                <div className="to-card__graphic" aria-hidden="true">
                    <TimeoutGraphic />
                </div>

                {/* Text */}
                <div className="to-card__text">
                    {/* <p className="to-card__eyebrow">Session ended</p> */}
                    <h1 className="to-card__title" id="to-title">
                        You've been signed out
                    </h1>
                    <p className="to-card__body" id="to-desc">
                        For your security, we sign you out after 2 hours of
                        inactivity. Your work is saved. Log back in to pick up
                        exactly where you left off.
                    </p>
                </div>

                {/* Action */}
                <button
                    type="button"
                    className="to-card__btn"
                    onClick={handleLogin}
                    disabled={isLoading}
                    autoFocus
                >
                    {isLoading ? "Redirecting…" : "Log back in"}
                </button>

                {/* Reassurance */}
                <p className="to-card__footnote">
                    {/* Questions?{" "} */}
                    <Link to="/help" className="to-card__link">
                        Help center
                    </Link>
                </p>
            </article>
        </div>
    );
}
